/**
 * PipelineHealth — portfolio-level pipeline rollup for the main dashboard.
 *
 * Replaces the per-claim AI Pipeline on the dashboard (that detail lives on the
 * case page). This is a ONE-GLANCE portfolio signal: how the whole book is
 * distributed across the pipeline, derived entirely from already-wired data
 * (reports + queueStats) — no new endpoint, no hardcoded data. Each segment
 * links into the matching filtered view.
 *
 * Token-based → renders in Daylight and Dark. Black + orange; green = done only.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

interface PipelineHealthProps {
  total: number;
  processing: number;
  needsReview: number;
  stalled: number;
  completed: number;
  onFilter?: (status: string) => void;
}

export default function PipelineHealth({ total, processing, needsReview, stalled, completed, onFilter }: PipelineHealthProps) {
  const navigate = useNavigate();

  // segment widths from real counts (avoid divide-by-zero)
  const denom = Math.max(1, processing + needsReview + stalled + completed);
  const seg = (n: number) => `${(n / denom) * 100}%`;

  const goProcessing = () => (onFilter ? onFilter('in_progress') : navigate('/dashboard?status=in_progress'));
  const goNeedsReview = () => navigate('/queue');
  const goStalled = () => (onFilter ? onFilter('stalled') : navigate('/dashboard?status=stalled'));
  const goCompleted = () => (onFilter ? onFilter('completed') : navigate('/dashboard?status=completed'));

  const metrics: { key: string; label: string; value: number; dot: string; valueColor: string; onClick: () => void }[] = [
    { key: 'processing', label: 'Processing', value: processing, dot: 'var(--color-brand)', valueColor: 'var(--color-needs-human-text)', onClick: goProcessing },
    { key: 'review', label: 'Need review', value: needsReview, dot: 'var(--color-brand)', valueColor: 'var(--color-needs-human-text)', onClick: goNeedsReview },
    { key: 'stalled', label: 'Stalled', value: stalled, dot: 'var(--color-text-dim)', valueColor: 'var(--color-text-secondary)', onClick: goStalled },
    { key: 'completed', label: 'Completed', value: completed, dot: 'var(--color-verified)', valueColor: 'var(--color-verified)', onClick: goCompleted },
  ];

  return (
    <div
      className="rounded-[14px] overflow-hidden border mb-6"
      style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
    >
      {/* header */}
      <div className="flex items-center gap-3" style={{ padding: '18px 22px', borderBottom: '1px solid var(--color-border-sub)' }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--color-needs-human-tint)', border: '1px solid rgba(255,102,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-brand)', fontSize: '1.125rem', flex: '0 0 38px' }}>◧</div>
        <div className="flex-1 min-w-0">
          <h2 className="font-serif" style={{ fontSize: '1.3125rem', color: 'var(--color-text-primary)', margin: 0, fontWeight: 400, lineHeight: 1.1 }}>Pipeline Health</h2>
          <div style={{ fontSize: '0.7812rem', color: 'var(--color-text-muted)', marginTop: 3 }}>
            <span className="font-mono" style={{ color: 'var(--color-text-primary)' }}>{total}</span> claims across the pipeline · live
          </div>
        </div>
        <button
          onClick={() => navigate('/orchestrator')}
          style={{ fontSize: '0.7812rem', fontWeight: 600, color: 'var(--color-needs-human-text)', background: 'transparent', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          Orchestrator →
        </button>
      </div>

      <div style={{ padding: '20px 22px' }}>
        {/* distribution bar */}
        <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', background: 'var(--color-bg-elevated)', marginBottom: 18 }}>
          {processing > 0 && <div title={`${processing} processing`} style={{ width: seg(processing), background: 'var(--color-brand)' }} />}
          {needsReview > 0 && <div title={`${needsReview} need review`} style={{ width: seg(needsReview), background: 'var(--color-brand-hover)' }} />}
          {stalled > 0 && <div title={`${stalled} stalled`} style={{ width: seg(stalled), background: 'var(--color-status-queued)' }} />}
          {completed > 0 && <div title={`${completed} completed`} style={{ width: seg(completed), background: 'var(--color-verified)' }} />}
        </div>

        {/* segments */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
          {metrics.map((m) => (
            <button
              key={m.key}
              onClick={m.onClick}
              className="text-left"
              style={{ background: 'var(--color-bg-base)', border: '1px solid var(--color-border)', borderRadius: 11, padding: '13px 15px', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.dot, flex: '0 0 7px' }} />
                <span style={{ fontSize: '0.7812rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>{m.label}</span>
              </div>
              <div className="font-mono" style={{ fontSize: '1.625rem', color: m.valueColor, fontWeight: 500, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{m.value}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
